import { useState } from 'react'
import Boton from './Boton'
import { whatsappCobrosApi } from '../api/whatsappCobros'
import { fmtFecha, fmtHoraDe } from '../utils/fecha'
import { colors, text, radius } from '../design/tokens'

/**
 * Los tres cobros por WhatsApp de un pedido web sin pagar (Martin, 26 sep
 * 2026): "WA 1 · WA 2 · WA 3", manuales, cada uno una sola vez, salen de la
 * línea del sistema como plantilla aprobada por Meta. Mismo componente en
 * Carritos abandonados y en el detalle de la NV — si cambia, cambia en los dos.
 *
 * Props:
 *  - pedidoId    id del pedido web
 *  - whatsapp    { telefono_e164, cobros:[{etapa, enviado_at, por, automatico, estado, error}], respondio_at, auto_respuesta_at,
 *                  automatico: { proximo:{etapa, at}|null, motivo } | null  (null = cobro automático apagado) }
 *  - plantillas  { pedido:{"1":"APPROVED"|…}, membresia:{…} } (opcional: si viene, una etapa
 *                sin aprobar queda deshabilitada; se mira el juego de whatsapp.tipo)
 *  - onActualizado(whatsapp)  se llama con lo que devuelve el POST
 *  - onError(mensaje)
 */
const ESTADO = {
  en_cola: { icono: '⏳', label: 'en cola', color: colors.textSec },
  enviado: { icono: '✓', label: 'enviado', color: colors.textSec },
  entregado: { icono: '✓✓', label: 'entregado', color: colors.textSec },
  leido: { icono: '✓✓', label: 'leído', color: '#0284c7' },
  fallido: { icono: '✕', label: 'falló', color: colors.err },
}

// Por qué el cobro automático no le escribe más a este pedido (motivos de
// CobroWhatsAppAutomaticoService::plan / RecuperoPedidosWebService).
const MOTIVO_AUTO = {
  pagado: 'ya pagó', pagado_sin_recupero: 'ya pagó', procesada: 'la NV ya se procesó',
  comprobante_cargado: 'cargó el comprobante', transferencia_avisada: 'avisó la transferencia',
  medio_sin_persecucion: 'eligió "a convenir"', nv_archivada: 'la NV está archivada',
  contesto: 'contestó: sigue Ventas', ultimo_fallido: 'el último WhatsApp falló',
  lista_negra: 'lista negra', ya_compro: 'ya compró por otro pedido', membresia_activa: 'ya es socio',
  ciclo_cumplido: 'terminó el ciclo', vencido: 'se pasó el horario', duplicado: 'pedido duplicado',
  sin_email: 'sin email', sin_nv: 'sin NV', desistio: 'desistió', completo: 'mandó los tres', mails_frenados: 'se frenaron los mails',
}

export default function BotonesCobroWA({ pedidoId, whatsapp, plantillas = null, onActualizado, onError, columnas = false }) {
  const [enviando, setEnviando] = useState(null)
  if (!whatsapp) return null

  const cobros = [1, 2, 3].map(e => (whatsapp.cobros || []).find(c => Number(c.etapa) === e) || { etapa: e })
  const sinTelefono = !whatsapp.telefono_e164
  const tipo = whatsapp.tipo || 'pedido'
  const estados = plantillas ? (plantillas[tipo] || {}) : null

  // El número al que SALE el mensaje, a la vista y corregible (Martin, 27 sep
  // 2026: "¿qué pasa cuando otro cliente cargue su número de forma diferente?").
  const legible = whatsapp.telefono_legible || (whatsapp.telefono_e164 ? `+${whatsapp.telefono_e164}` : null)
  const corregirTelefono = async () => {
    const nuevo = window.prompt(
      sinTelefono
        ? `El teléfono del pedido no se entiende como un número válido:\n"${whatsapp.telefono_original || ''}"\n\nEscribí el número correcto (con área; ej: 11 6419 9868 o +54 9 11 6419 9868):`
        : `Va a salir al ${legible}\n(cargado como "${whatsapp.telefono_original || ''}")\n\nSi no es el número correcto, escribilo acá:`,
      whatsapp.telefono_original || '',
    )
    if (nuevo == null || !nuevo.trim() || nuevo.trim() === (whatsapp.telefono_original || '')) return
    try {
      const { data } = await whatsappCobrosApi.telefono(pedidoId, nuevo.trim())
      onActualizado?.(data.whatsapp)
    } catch (err) {
      onError?.(err.response?.data?.message || 'No se pudo cambiar el teléfono.')
    }
  }

  // Sin confirmación (Martin, 30 sep 2026): el click en el botón manda. El
  // número de destino ya está a la vista al lado, con su lápiz.
  const enviar = async (etapa) => {
    setEnviando(etapa)
    try {
      const { data } = await whatsappCobrosApi.enviar(pedidoId, etapa)
      onActualizado?.(data.whatsapp)
    } catch (err) {
      onError?.(err.response?.data?.message || 'No se pudo mandar el WhatsApp.')
    } finally { setEnviando(null) }
  }

  // `columnas` (tabla de Carritos, Martin 26 sep 2026: "ordená bien los tags en
  // columnas"): los tres WA en una grilla fija para que queden alineados entre
  // filas, y los tags de estado en una segunda línea. Sin `columnas` (NV), en línea.
  // Fecha y hora de cada aviso A LA VISTA debajo de su botón (Martin, 30 sep
  // 2026: "me falta saber en qué fecha y hora se mandó cada aviso") — antes
  // vivía solo en el tooltip. Un fallido muestra cuándo se intentó.
  // El próximo que va a mandar el automático: su fecha y hora debajo del botón, en verde.
  const proximo = whatsapp.automatico?.proximo
  const cuandoDe = c => {
    if (c.enviado_at) {
      return (
        <span style={{ ...st.cuando, ...(c.estado === 'fallido' ? { color: colors.err } : {}) }}>
          {fmtFecha(c.enviado_at)}<br />{fmtHoraDe(c.enviado_at)}
        </span>
      )
    }
    if (proximo && Number(proximo.etapa) === c.etapa) {
      const vencido = new Date(proximo.at) <= new Date()
      return (
        <span style={{ ...st.cuando, color: colors.ok }} title="Lo manda solo el cobro automático">
          🤖 {vencido ? 'ya sale' : <>{fmtFecha(proximo.at)}<br />{fmtHoraDe(proximo.at)}</>}
        </span>
      )
    }
    return null
  }
  const botones = cobros.map(c => (
    <span key={c.etapa} style={st.celda}>
      {boton(c)}
      {cuandoDe(c)}
    </span>
  ))
  function boton(c) {
        // Un envío que Meta rechazó (fallido) NO cuenta como mandado: vuelve a
        // ser botón, con el error a la vista, y el backend lo deja reintentar.
        if (c.enviado_at && c.estado !== 'fallido') {
          const es = ESTADO[c.estado] || ESTADO.enviado
          const cuando = `${fmtFecha(c.enviado_at)} ${fmtHoraDe(c.enviado_at)}`
          return (
            <span
              key={c.etapa}
              style={{ ...st.chip, color: es.color, textAlign: 'center' }}
              title={`WA ${c.etapa} — ${es.label} · ${cuando}${c.automatico ? ' · lo mandó el cobro automático' : c.por ? ` · por ${c.por}` : ''}${c.click_at ? ` · abrió el link ${fmtFecha(c.click_at)} ${fmtHoraDe(c.click_at)}` : ''}${c.error ? ` · ${c.error}` : ''}`}
            >
              {c.automatico ? '🤖 ' : ''}WA {c.etapa} {es.icono}{c.click_at ? ' 🔗' : ''}
            </span>
          )
        }
        const aprobada = !estados || estados[String(c.etapa)] === 'APPROVED'
        const fallo = c.estado === 'fallido'
        const title = sinTelefono
          ? 'El pedido no tiene teléfono'
          : !aprobada
            ? `Mensaje ${c.etapa}: plantilla no aprobada en Meta`
            : fallo
              ? `⚠ No se envió: ${c.error || 'Meta lo rechazó'} — click para reintentar`
              : `Mandar el mensaje de cobro ${c.etapa} por WhatsApp desde la línea del sistema`
        return (
          <Boton
            key={c.etapa}
            variante="send" sm
            disabled={sinTelefono || !aprobada || enviando !== null}
            title={title}
            onClick={() => enviar(c.etapa)}
          >
            {enviando === c.etapa ? '…' : fallo ? `WA ${c.etapa} ⚠` : `WA ${c.etapa}`}
          </Boton>
        )
  }
  const tags = (
    <>
      {tipo === 'membresia' && (
        <span style={{ ...st.chip, color: colors.t80Text, background: colors.t80Bg, borderColor: colors.t80Border }}
          title="El pedido trae una membresía del Wine Club: se usan las plantillas de membresías">
          membresía
        </span>
      )}
      {whatsapp.respondio_at && (
        <span style={{ ...st.chip, color: colors.ok, background: colors.okBg, borderColor: '#bbf7d0' }}
          title={`El cliente respondió por WhatsApp ${fmtFecha(whatsapp.respondio_at)} ${fmtHoraDe(whatsapp.respondio_at)}`}>
          💬 respondió
        </span>
      )}
      {whatsapp.automatico?.motivo && whatsapp.automatico.motivo !== 'completo' && (
        <span style={st.chip} title="El cobro automático por WhatsApp no le escribe más a este pedido">
          🤖 no sigue: {MOTIVO_AUTO[whatsapp.automatico.motivo] || whatsapp.automatico.motivo}
        </span>
      )}
      {whatsapp.auto_respuesta_at && (
        <span style={st.chip}
          title={`LGR le contestó solo con el link a Ventas ${fmtFecha(whatsapp.auto_respuesta_at)} ${fmtHoraDe(whatsapp.auto_respuesta_at)}`}>
          ↩ le contestamos
        </span>
      )}
    </>
  )

  // A qué número sale (o que no hay uno válido), con el lápiz para corregirlo.
  const numero = (
    <button
      type="button"
      onClick={corregirTelefono}
      style={{ ...st.numero, ...(sinTelefono ? st.numeroMal : {}) }}
      title={sinTelefono ? 'El teléfono del pedido no se entiende como un número válido — click para corregirlo' : `Cargado como "${whatsapp.telefono_original || ''}" — click para corregir`}
    >
      {sinTelefono ? `⚠ Teléfono inválido${whatsapp.telefono_original ? ` ("${whatsapp.telefono_original}")` : ''}` : `→ ${legible}`} ✎
    </button>
  )

  if (columnas) {
    return (
      <div style={st.columnas} onClick={e => e.stopPropagation()}>
        {numero}
        <div style={st.grilla}>{botones}</div>
        <div style={st.tags}>{tags}</div>
      </div>
    )
  }

  return (
    <span style={columnas ? st.columnas : st.grupo} onClick={e => e.stopPropagation()}>
      {numero}
      {botones}
      {tags}
    </span>
  )
}

const st = {
  grupo: { display: 'inline-flex', alignItems: 'flex-start', gap: 4, flexWrap: 'wrap' },
  numero: {
    border: 'none', background: 'transparent', padding: '2px 4px', cursor: 'pointer',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap',
  },
  numeroMal: { color: colors.err, fontFamily: 'inherit', fontWeight: 600 },
  columnas: { display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' },
  // Tres celdas del mismo ancho: WA 1 · 2 · 3 alineados fila contra fila.
  grilla: { display: 'grid', gridTemplateColumns: 'repeat(3, 72px)', gap: 4, alignItems: 'start' },
  celda: { display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  cuando: { fontSize: 11, lineHeight: 1.2, color: colors.textSec, textAlign: 'center', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  tags: { display: 'flex', gap: 4, flexWrap: 'wrap', minHeight: 0 },
  // Cobro ya mandado = chip gris no clickeable (patrón "⏳ Enviado" de Pagos)
  chip: {
    display: 'inline-block', padding: '3px 8px', background: colors.bgSoft,
    border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    fontSize: text.sec, fontWeight: 600, color: colors.textSec, whiteSpace: 'nowrap', cursor: 'default',
  },
}
